<template>
  <div class="flex flex-col items-center py-8 px-4">
    <div class="w-full max-w-lg">

      <!-- Header -->
      <div class="mb-6">
        <h2 class="text-base font-semibold text-gray-800">Update Annual Returns Status</h2>
        <p class="text-sm text-gray-500 mt-1">
          Use this form to manually correct the annual returns filing status for a registered entity.
          Select the relevant years and set the target status to reflect the correct state in the system.
        </p>
      </div>

      <!-- Card -->
      <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <form @submit.prevent="onSubmit" class="space-y-4">

          <!-- BRN -->
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Business Registration
              Number</label>
            <input type="text" v-model="form.brn" placeholder="e.g. 80000012345678" required
                   class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50" />
          </div>

          <!-- Rationale -->
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Rationale</label>
            <textarea v-model="form.rationale" placeholder="Briefly explain the reason for this correction..." required
                      class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 h-24 resize-none"></textarea>
          </div>

          <!-- Year selector + target status -->
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1">
              <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Add Year</label>
              <select v-model="selectedYear"
                      class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
                <option v-for="option in yearOptions" :key="option.value" :value="option.value">{{
                    option.text
                  }}
                </option>
              </select>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Target Status</label>
              <select v-model="form.targetStatus"
                      class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
                <option :value="null">-- Select Status --</option>
                <option value="FILED">Filed</option>
                <option value="NOT_FILED">Not Filed</option>
                <option value="OVERDUE">Overdue</option>
              </select>
            </div>
          </div>

          <!-- Selected years -->
          <div v-if="form.years.length > 0">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Selected Years</label>
            <div class="flex flex-wrap gap-2 mt-2 bg-gray-50 border border-gray-200 rounded-md p-3">
              <span v-for="(y, idx) in form.years" :key="idx"
                    class="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded text-xs font-medium">
                {{ y }}
                <i class="fa fa-times cursor-pointer hover:text-blue-900" @click="removeYear(idx)"></i>
              </span>
            </div>
          </div>

          <!-- Divider -->
          <div class="border-t border-gray-100 pt-2 flex justify-end">
            <button type="submit" class="btn-primary" :disabled="loading">
              <i class="fa fa-check mr-1.5"></i> Apply Changes
            </button>
          </div>

        </form>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { years } from "@/commons/years.js";
import { useInternalProcessesStore } from "../stores";

const store = useInternalProcessesStore();

const loading = ref(false);
const selectedYear = ref<number | null>(null);
const form = ref({
  brn: null as string | null,
  rationale: null as string | null,
  years: [] as number[],
  targetStatus: null as string | null
});
const yearOptions = years;

const resetForm = () => {
  form.value = { brn: null, rationale: null, years: [], targetStatus: null };
};

const removeYear = (index: number) => {
  form.value.years.splice(index, 1);
};

const onSubmit = () => {
  loading.value = true;
  store.updateAnnualReturnStatus(form.value);
};

watch(selectedYear, (year) => {
  if (year !== null) form.value.years.push(year);
});

watch(() => store.annualReturnsStatusChange, (data) => {
  if (data) {
    loading.value = false;
    resetForm();
  }
});

watch(() => store.annualReturnsStatusError, () => {
  loading.value = false;
});
</script>
